import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  RectangleGroupIcon,
  CloudIcon,
  SparklesIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  PlayIcon,
  PhotoIcon,
  SpeakerWaveIcon,
  LinkIcon,
  MicrophoneIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Import existing components
import WhiteboardCanvas from './WhiteboardCanvas';
import LoadingSpinner from './LoadingSpinner';

const HybridWhiteboard = ({ boardId, user, board, onContentCreate }) => {
  const location = useLocation();
  const miroFrameRef = useRef(null);
  
  // Mode state - check URL parameter first
  const [whiteboardMode, setWhiteboardMode] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('mode') === 'miro' ? 'miro' : 'internal';
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Miro state
  const [miroConnected, setMiroConnected] = useState(false);
  const [miroAuthUrl, setMiroAuthUrl] = useState('');
  const [miroBoard, setMiroBoard] = useState(null);
  const [miroEmbedUrl, setMiroEmbedUrl] = useState('');
  const [showMiroSetup, setShowMiroSetup] = useState(false);
  
  // Internal canvas state
  const [canvasBlocks, setCanvasBlocks] = useState([]);
  const [chatNode, setChatNode] = useState(null);
  const [connections, setConnections] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  
  // AI Chat state (unified for both modes)
  const [showAIPanel, setShowAIPanel] = useState(true);
  const [selectedModel, setSelectedModel] = useState('gpt-4');
  const [chatMessages, setChatMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isAISending, setIsAISending] = useState(false);
  
  // Content creation tools
  const [showContentTools, setShowContentTools] = useState(false);
  
  // AI Models
  const aiModels = [
    { id: 'gpt-4', name: 'GPT-4', provider: 'OpenAI' },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', provider: 'OpenAI' },
    { id: 'claude-3', name: 'Claude 3', provider: 'Anthropic' },
    { id: 'gemini-pro', name: 'Gemini Pro', provider: 'Google' },
    { id: 'llama-2', name: 'Llama 2', provider: 'Meta' },
    { id: 'mistral-7b', name: 'Mistral 7B', provider: 'Mistral' }
  ];

  // Content types for creation tools
  const contentTypes = [
    { id: 'note', name: 'Text Note', icon: DocumentTextIcon, color: 'blue' },
    { id: 'video', name: 'Video', icon: PlayIcon, color: 'red' },
    { id: 'image', name: 'Image', icon: PhotoIcon, color: 'green' },
    { id: 'audio', name: 'Audio', icon: SpeakerWaveIcon, color: 'yellow' },
    { id: 'link', name: 'Link', icon: LinkIcon, color: 'purple' },
    { id: 'voice', name: 'Voice Note', icon: MicrophoneIcon, color: 'pink' }
  ];

  useEffect(() => {
    initializeHybridSystem();
  }, [boardId]);

  useEffect(() => {
    // Handle OAuth callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const miroStatus = urlParams.get('miro');
    
    if (miroStatus === 'connected') {
      toast.success('Miro connected successfully!');
      setMiroConnected(true);
      setShowMiroSetup(false);
      setWhiteboardMode('miro');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (miroStatus === 'error') {
      toast.error('Miro connection failed');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    // Update content tools visibility based on mode
    setShowContentTools(whiteboardMode === 'internal');
  }, [whiteboardMode]);

  const initializeHybridSystem = async () => {
    try {
      setIsLoading(true);
      
      await Promise.all([
        checkMiroConnection(),
        loadInternalCanvas(),
        loadChatHistory()
      ]);
      
    } catch (error) {
      console.error('Failed to initialize hybrid system:', error);
      toast.error('Failed to initialize whiteboard');
    } finally {
      setIsLoading(false);
    }
  };

  const checkMiroConnection = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/miro/auth/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMiroConnected(data.connected);
        setMiroAuthUrl(data.authUrl);
        
        if (data.connected) {
          await loadMiroBoard();
        } else if (whiteboardMode === 'miro') {
          setShowMiroSetup(true);
        }
      }
    } catch (error) {
      console.error('Failed to check Miro connection:', error);
    }
  };

  const loadMiroBoard = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/miro/boards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${board?.name || 'Alohai Board'} - ${boardId}`,
          description: 'Hybrid whiteboard with AI integration'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMiroBoard(data.board);
        
        if (data.board && data.board.id) {
          const embedUrl = `https://miro.com/app/live-embed/${data.board.id}/?&embedAutoplay=true`;
          setMiroEmbedUrl(embedUrl);
          console.log('Miro embed URL:', embedUrl);
        }
      }
    } catch (error) {
      console.error('Failed to load Miro board:', error);
      toast.error('Failed to initialize Miro board');
    }
  };

  const loadInternalCanvas = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/whiteboard/${boardId}/blocks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCanvasBlocks(data.blocks || []);
        setChatNode(data.chatNode);
        setConnections(data.connections || []);
        
        // If no blocks exist, create some demo blocks for testing
        if (!data.blocks || data.blocks.length === 0) {
          console.log('No blocks found, creating demo blocks...');
          createDemoBlocks();
        }
      } else {
        console.log('Failed to load blocks, creating demo blocks...');
        createDemoBlocks();
      }
    } catch (error) {
      console.error('Failed to load internal canvas:', error);
      console.log('Error loading blocks, creating demo blocks...');
      createDemoBlocks();
    }
  };

  // Function to create demo blocks for testing
  const createDemoBlocks = () => {
    const demoBlocks = [
      {
        id: 'demo-1',
        board_id: boardId,
        block_type: 'note',
        title: 'Welcome Note',
        content_text: 'Welcome to the internal canvas! This is a demo note block. Double-click to edit, drag to move, and drag corners to resize.',
        position_x: 100,
        position_y: 100,
        width: 280,
        height: 180,
        z_index: 1
      },
      {
        id: 'demo-2',
        board_id: boardId,
        block_type: 'video',
        title: 'Video Block',
        content_text: 'This is a video block. You can add video URLs here.',
        content_url: 'https://example.com/video.mp4',
        position_x: 400,
        position_y: 120,
        width: 320,
        height: 240,
        z_index: 1
      },
      {
        id: 'demo-3',
        board_id: boardId,
        block_type: 'image',
        title: 'Image Block',
        content_text: 'This is an image block for visual content.',
        position_x: 750,
        position_y: 80,
        width: 280,
        height: 200,
        z_index: 1
      }
    ];

    const demoChatNode = {
      id: 'demo-chat',
      board_id: boardId,
      position_x: 200,
      position_y: 400,
      width: 360,
      height: 200,
      z_index: 999
    };

    console.log('Setting demo blocks:', demoBlocks);
    setCanvasBlocks(demoBlocks);
    setChatNode(demoChatNode);
    setConnections([]);
  };

  const loadChatHistory = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/whiteboard/${boardId}/chat`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setChatMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const switchMode = async (newMode) => {
    if (newMode === whiteboardMode) return;
    
    setIsTransitioning(true);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (newMode === 'miro' && !miroConnected) {
      setShowMiroSetup(true);
    } else {
      setWhiteboardMode(newMode);
      
      const url = new URL(window.location);
      if (newMode === 'miro') {
        url.searchParams.set('mode', 'miro');
      } else {
        url.searchParams.delete('mode');
      }
      window.history.pushState({}, '', url);
      
      toast.success(`Switched to ${newMode === 'miro' ? 'Miro Professional' : 'Internal Canvas'} mode`);
    }
    
    setIsTransitioning(false);
  };

  const handleMiroAuth = () => {
    if (miroAuthUrl) {
      localStorage.removeItem('miro_auth_status');
      
      const popup = window.open(miroAuthUrl, '_blank', 'width=600,height=700');
      
      const handleMessage = (event) => {
        const allowedOrigins = [
          'https://alohai-prod.william-4c8.workers.dev',
          import.meta.env.VITE_API_URL
        ].filter(Boolean);
        
        const isAllowedOrigin = allowedOrigins.some(origin => 
          event.origin === origin || event.origin.includes('alohai-prod.william-4c8.workers.dev')
        );
        
        if (!isAllowedOrigin) return;

        if (event.data.type === 'MIRO_AUTH_SUCCESS') {
          setMiroConnected(true);
          setShowMiroSetup(false);
          setWhiteboardMode('miro');
          
          loadMiroBoard();
          
          toast.success('Connected to Miro successfully!');
          cleanup();
        } else if (event.data.type === 'MIRO_AUTH_ERROR') {
          toast.error(`Miro authentication failed: ${event.data.error}`);
          cleanup();
        }
      };

      const cleanup = () => {
        window.removeEventListener('message', handleMessage);
        if (popup && !popup.closed) {
          popup.close();
        }
      };

      window.addEventListener('message', handleMessage);
      setTimeout(cleanup, 60000);
    }
  };

  const sendAIMessage = async () => {
    if (!currentMessage.trim() || isAISending) return;
    
    const userMessage = currentMessage.trim();
    setCurrentMessage('');
    setIsAISending(true);
    
    const newMessages = [...chatMessages, {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }];
    setChatMessages(newMessages);
    
    try {
      let context = '';
      if (whiteboardMode === 'internal') {
        context = canvasBlocks
          .filter(block => selectedBlocks.includes(block.id))
          .map(block => `${block.block_type}: ${block.content_text || block.content_url || 'No content'}`)
          .join('\n');
      } else {
        context = `Miro whiteboard mode - Professional collaborative whiteboard. Board: ${miroBoard?.name || 'Collaborative Board'}`;
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/whiteboard/${boardId}/chat/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage,
          model: selectedModel,
          context: context,
          mode: whiteboardMode
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'assistant',
          content: data.response,
          model: selectedModel,
          timestamp: new Date().toISOString()
        }]);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send AI message:', error);
      toast.error('Failed to send message');
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'error',
        content: 'Failed to send message. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsAISending(false);
    }
  };

  const createContent = async (contentType) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/whiteboard/${boardId}/blocks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          block_type: contentType,
          title: `New ${contentType}`,
          content_text: `Enter your ${contentType} content here...`,
          position_x: Math.random() * 400 + 100,
          position_y: Math.random() * 300 + 100,
          width: 250,
          height: 150
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCanvasBlocks(prev => [...prev, data.block]);
        toast.success(`${contentType} block created`);
        
        if (onContentCreate) {
          onContentCreate(data.block);
        }
      }
    } catch (error) {
      console.error('Failed to create content:', error);
      toast.error('Failed to create content');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="text-gray-600 mt-4">Initializing hybrid whiteboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mode Selection Sidebar */}
      <div className="w-16 bg-white border-r border-gray-200 flex flex-col items-center py-4 space-y-4">
        <motion.button
          onClick={() => switchMode('internal')}
          className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
            whiteboardMode === 'internal' 
              ? 'bg-purple-100 text-purple-600' 
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Internal Canvas"
        >
          <RectangleGroupIcon className="w-6 h-6" />
        </motion.button>

        <motion.button
          onClick={() => switchMode('miro')}
          className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all relative ${
            whiteboardMode === 'miro' 
              ? 'bg-blue-100 text-blue-600' 
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Miro Professional"
        >
          <CloudIcon className="w-6 h-6" />
          {miroConnected && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
          )}
        </motion.button>

        <div className="flex-1"></div>

        <motion.button
          className="w-12 h-12 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Settings"
        >
          <Cog6ToothIcon className="w-6 h-6" />
        </motion.button>
      </div>

      {/* Content Tools Sidebar */}
      <AnimatePresence>
        {showContentTools && whiteboardMode === 'internal' && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 240, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border-r border-gray-200 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Content Tools</h3>
                <button
                  onClick={() => setShowContentTools(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-2">
                {contentTypes.map((type) => (
                  <motion.button
                    key={type.id}
                    onClick={() => createContent(type.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                      <type.icon className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {type.name}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Whiteboard Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {board?.name || 'Hybrid Whiteboard'}
                </h1>
                <p className="text-sm text-gray-500">
                  {whiteboardMode === 'miro' ? 'Professional Miro Collaboration' : 'Internal Visual Canvas'} • AI-Powered
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {whiteboardMode === 'internal' && !showContentTools && (
                <motion.button
                  onClick={() => setShowContentTools(true)}
                  className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  whileHover={{ scale: 1.05 }}
                >
                  Show Tools
                </motion.button>
              )}
              
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                whiteboardMode === 'miro' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-purple-100 text-purple-700'
              }`}>
                <SparklesIcon className="w-4 h-4" />
                <span>{whiteboardMode === 'miro' ? 'Miro Pro' : 'Canvas Pro'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <div className="flex-1 relative">
          <AnimatePresence mode="wait">
            {isTransitioning ? (
              <motion.div
                key="transition"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center bg-gray-50"
              >
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Switching modes...</p>
                </div>
              </motion.div>
            ) : whiteboardMode === 'miro' ? (
              <motion.div
                key="miro"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                className="absolute inset-0"
              >
                {miroConnected && miroEmbedUrl ? (
                  <div className="h-full w-full">
                    <iframe
                      ref={miroFrameRef}
                      src={miroEmbedUrl}
                      className="w-full h-full border-0"
                      allow="microphone; camera; fullscreen; clipboard-read; clipboard-write"
                      title="Miro Professional Whiteboard"
                      style={{
                        minHeight: '100%',
                        backgroundColor: 'white'
                      }}
                      onLoad={() => {
                        console.log('Miro embed loaded successfully');
                        toast.success('Miro whiteboard loaded!');
                      }}
                      onError={() => {
                        console.error('Miro embed failed to load');
                        toast.error('Failed to load Miro whiteboard');
                      }}
                    />
                  </div>
                ) : miroConnected ? (
                  <div className="h-full bg-white flex items-center justify-center">
                    <div className="text-center">
                      <CloudIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Initializing Miro Board</h3>
                      <p className="text-gray-600 mb-4">Setting up your professional whiteboard...</p>
                      <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full bg-gray-50 flex items-center justify-center">
                    <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
                      <CloudIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Connect to Miro</h3>
                      <p className="text-gray-600 mb-6">
                        Connect your Miro account to access professional whiteboard features with real-time collaboration.
                      </p>
                      <div className="space-y-4">
                        <motion.button
                          onClick={handleMiroAuth}
                          className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Connect to Miro
                        </motion.button>
                        
                        <motion.button
                          onClick={() => switchMode('internal')}
                          className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Use Internal Canvas Instead
                        </motion.button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="internal"
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                className="absolute inset-0"
              >
                <WhiteboardCanvas
                  boardId={boardId}
                  canvasBlocks={canvasBlocks}
                  chatNode={chatNode}
                  connections={connections}
                  onBlockCreate={(block) => setCanvasBlocks(prev => [...prev, block])}
                  onBlockUpdate={(blockId, updates) => {
                    setCanvasBlocks(prev => prev.map(b => b.id === blockId ? {...b, ...updates} : b));
                  }}
                  onBlockDelete={(blockId) => {
                    setCanvasBlocks(prev => prev.filter(b => b.id !== blockId));
                  }}
                  onBlockConnect={(blockId) => {
                    setSelectedBlocks(prev => 
                      prev.includes(blockId) 
                        ? prev.filter(id => id !== blockId)
                        : [...prev, blockId]
                    );
                  }}
                  onChatMessage={sendAIMessage}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* AI Chat Panel */}
      <AnimatePresence>
        {showAIPanel && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 400, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border-l border-gray-200 flex flex-col overflow-hidden"
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-purple-600" />
                  AI Assistant
                </h3>
                <button
                  onClick={() => setShowAIPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className={`text-xs px-2 py-1 rounded-full text-center mb-3 ${
                whiteboardMode === 'miro' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {whiteboardMode === 'miro' ? 'Miro Mode' : 'Canvas Mode'}
              </div>
              
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              >
                {aiModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} ({model.provider})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl ${
                      message.type === 'user'
                        ? 'bg-purple-600 text-white'
                        : message.type === 'error'
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    {message.model && (
                      <p className="text-xs opacity-70 mt-1">
                        {message.model}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              
              {isAISending && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 p-3 rounded-2xl">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <textarea
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendAIMessage();
                      }
                    }}
                    placeholder={`Ask AI about your ${whiteboardMode === 'miro' ? 'Miro board' : 'canvas content'}...`}
                    className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows="2"
                  />
                </div>
                <motion.button
                  onClick={sendAIMessage}
                  disabled={!currentMessage.trim() || isAISending}
                  className="p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ChatBubbleLeftRightIcon className="w-5 h-5" />
                </motion.button>
              </div>
              
              {whiteboardMode === 'internal' && selectedBlocks.length > 0 && (
                <p className="text-xs text-purple-600 mt-2">
                  Using context from {selectedBlocks.length} selected block{selectedBlocks.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!showAIPanel && (
        <motion.button
          onClick={() => setShowAIPanel(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
        >
          <SparklesIcon className="w-6 h-6" />
        </motion.button>
      )}
    </div>
  );
};

export default HybridWhiteboard;
